/**
 * Configuração Otimizada de Cache para o Sistema CRM
 *
 * Este arquivo centraliza todas as configurações de cache para resolver
 * os problemas identificados na análise:
 * - TTL inadequado (5s -> estratégico por tipo)
 * - Falta de retry e error handling
 * - Configurações inconsistentes
 *
 * @since 2025-10-03
 */

import type { QueryClient } from "@tanstack/vue-query";
import { keepPreviousData } from "@tanstack/vue-query";

// Exportar utilitários de invalidação
export { invalidateQueriesOptimized, invalidateMultipleQueries, invalidateQueriesWithDelay } from "./query-invalidation-utils";

// ========================================
// CONSTANTES DE TEMPO DE CACHE
// ========================================
export const CACHE_TIMES = {
  // Dados estáticos - raramente mudam
  STATIC_DATA: 1000 * 60 * 30, // 30 minutos

  // Dados de usuário - mudam moderadamente
  USER_DATA: 1000 * 60 * 10, // 10 minutos

  // Dados dinâmicos - mudam frequentemente
  DYNAMIC_DATA: 1000 * 60 * 2, // 2 minutos

  // Dados críticos - sempre frescos
  CRITICAL_DATA: 1000 * 30, // 30 segundos

  // Dados de paginação - otimizado para performance
  PAGINATION_DATA: 1000 * 60 * 5, // 5 minutos

  // Cache de GC (garbage collection)
  GC_TIME: 1000 * 60 * 60, // 1 hora
} as const;

// ========================================
// CONFIGURAÇÃO DO QUERY CLIENT
// ========================================
export const queryClientConfig = {
  defaultOptions: {
    queries: {
      // Cache estratégico por dados
      staleTime: CACHE_TIMES.USER_DATA,
      gcTime: CACHE_TIMES.GC_TIME,

      // Retry inteligente
      retry: (failureCount: number, error: unknown) => {
        // Não retry em erros 4xx (cliente)
        if (error && typeof error === "object" && "status" in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Máximo 3 tentativas para erros de rede
        return failureCount < 3;
      },

      // Background refetch para melhor UX
      refetchOnWindowFocus: false, // Evita refetch desnecessário
      refetchOnReconnect: true,

      // Retry delay progressivo
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },

    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
};

// ========================================
// FACTORY DE QUERY KEYS ESTRUTURADAS
// ========================================
export const QUERY_KEYS = {
  // Customers
  customers: {
    all: ["customers"] as const,
    lists: () => [...QUERY_KEYS.customers.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...QUERY_KEYS.customers.lists(), filters] as const,
    listComplete: (filters?: Record<string, unknown>) => [...QUERY_KEYS.customers.lists(), "complete", filters] as const,
    archived: (filters?: Record<string, unknown>) => [...QUERY_KEYS.customers.all, "archived", filters] as const,
    details: () => [...QUERY_KEYS.customers.all, "detail"] as const,
    detail: (id: string) => [...QUERY_KEYS.customers.details(), id] as const,
    processes: (id: string) => [...QUERY_KEYS.customers.detail(id), "processes"] as const,
    phones: (id: string) => [...QUERY_KEYS.customers.detail(id), "phones"] as const,
    documents: (id: string) => [...QUERY_KEYS.customers.detail(id), "documents"] as const,
    libraryDocuments: (id: string) => [...QUERY_KEYS.customers.detail(id), "library-documents"] as const,
  },

  // Members/Users
  members: {
    all: ["members"] as const,
    lists: () => [...QUERY_KEYS.members.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...QUERY_KEYS.members.lists(), filters] as const,
    detail: (id: string) => [...QUERY_KEYS.members.all, "detail", id] as const,
  },

  // Precatories
  precatories: {
    all: ["precatories"] as const,
    lists: () => [...QUERY_KEYS.precatories.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...QUERY_KEYS.precatories.lists(), filters] as const,
    detail: (id: string, isArchived?: boolean) => [...QUERY_KEYS.precatories.all, "detail", id, isArchived] as const,
  },

  // Calendar
  calendar: {
    all: ["calendar"] as const,
    events: () => [...QUERY_KEYS.calendar.all, "events"] as const,
    event: (id: string) => [...QUERY_KEYS.calendar.events(), id] as const,
    eventsList: (filters?: Record<string, unknown>) => [...QUERY_KEYS.calendar.events(), filters] as const,
  },

  // PJE
  pje: {
    all: ["pje"] as const,
    processes: () => [...QUERY_KEYS.pje.all, "processes"] as const,
    process: (id: string) => [...QUERY_KEYS.pje.processes(), id] as const,
    processList: (filters?: Record<string, unknown>) => [...QUERY_KEYS.pje.processes(), filters] as const,
    sync: (id: string) => [...QUERY_KEYS.pje.process(id), "sync"] as const,
    customerSearch: (search: string) => [...QUERY_KEYS.pje.all, "customer-search", search] as const,
    customerId: (id: number) => [...QUERY_KEYS.pje.all, "customer", id] as const,
    sendToken: (id: string) => [...QUERY_KEYS.pje.all, "send-token", id] as const,
  },

  // Notifications
  notifications: {
    all: ["notifications"] as const,
    list: () => [...QUERY_KEYS.notifications.all, "list"] as const,
  },

  // Publications
  publications: {
    all: ["publications"] as const,
    lists: () => [...QUERY_KEYS.publications.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...QUERY_KEYS.publications.lists(), filters] as const,
  },

  // Logs Chamada
  logsChamada: {
    all: ["logs-chamada"] as const,
    lists: () => [...QUERY_KEYS.logsChamada.all, "list"] as const,
    listWithFilters: (filters?: Record<string, unknown>) => [...QUERY_KEYS.logsChamada.lists(), filters] as const,
    list: (customerUuid: string, filters?: Record<string, unknown>) => [...QUERY_KEYS.logsChamada.all, customerUuid, filters] as const,
    customer: (customerUuid: string) => [...QUERY_KEYS.logsChamada.all, "customer", customerUuid] as const,
    home: () => [...QUERY_KEYS.logsChamada.all, "home"] as const,
    homeWeek: () => [...QUERY_KEYS.logsChamada.home(), "week"] as const,
    homeMonth: () => [...QUERY_KEYS.logsChamada.home(), "month"] as const,
  },
} as const;

// ========================================
// CONFIGURAÇÕES POR MÓDULO
// ========================================
export const MODULE_CACHE_CONFIG = {
  // Dados de autenticação - críticos
  auth: {
    staleTime: CACHE_TIMES.CRITICAL_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },

  // Lista de clientes - dinâmicos
  customers: {
    staleTime: CACHE_TIMES.DYNAMIC_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },

  // Detalhes do cliente - moderado
  customerDetail: {
    staleTime: CACHE_TIMES.USER_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },

  // Dados de formulário - estáticos
  formOptions: {
    staleTime: CACHE_TIMES.STATIC_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },

  // Calendário - dinâmico
  calendar: {
    staleTime: CACHE_TIMES.DYNAMIC_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },

  // PJE sync - crítico
  pjeSync: {
    staleTime: CACHE_TIMES.CRITICAL_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },
} as const;

// ========================================
// UTILITÁRIOS DE INVALIDAÇÃO
// ========================================
export class CacheInvalidator {
  constructor(private queryClient: QueryClient) {}

  // Invalida todos os dados de um cliente
  invalidateCustomer(customerId: string) {
    return Promise.all([
      this.queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.detail(customerId),
      }),
      this.queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.lists(),
      }),
    ]);
  }

  // Invalida dados relacionados após mutations
  async invalidateRelatedData(type: "customer" | "member" | "precatory", id?: string) {
    const invalidationMap = {
      customer: () => (id ? this.invalidateCustomer(id) : this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customers.all })),
      member: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members.all }),
      precatory: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.precatories.all }),
    };

    return invalidationMap[type]();
  }

  // Limpeza completa do cache
  clearAllCache() {
    return this.queryClient.clear();
  }

  // Remove cache específico por padrão
  removeByPattern(pattern: string[]) {
    return this.queryClient.invalidateQueries({ queryKey: pattern });
  }
}

// ========================================
// MONITORAMENTO DE CACHE
// ========================================
export class CacheMonitor {
  private stats = {
    hits: 0,
    misses: 0,
    invalidations: 0,
    errors: 0,
  };

  logCacheHit(_queryKey: string[]) {
    this.stats.hits++;
  }

  logCacheMiss(_queryKey: string[]) {
    this.stats.misses++;
  }

  logInvalidation(_queryKey: string[]) {
    this.stats.invalidations++;
  }

  logError(_error: unknown, _queryKey: string[]) {
    this.stats.errors++;
  }

  getStats() {
    return { ...this.stats };
  }

  resetStats() {
    this.stats = { hits: 0, misses: 0, invalidations: 0, errors: 0 };
  }
}

// ========================================
// HELPERS PARA CONFIGURAÇÃO RÁPIDA
// ========================================
export const createQueryConfig = (type: keyof typeof MODULE_CACHE_CONFIG) => {
  return MODULE_CACHE_CONFIG[type];
};

export const createListQuery = (baseConfig: typeof MODULE_CACHE_CONFIG.customers) => ({
  ...baseConfig,
  placeholderData: (prev: unknown) => prev, // Keep previous data
});

export const createDetailQuery = (baseConfig: typeof MODULE_CACHE_CONFIG.customerDetail) => ({
  ...baseConfig,
  staleTime: baseConfig.staleTime * 2, // Details cache longer
});

export const QUERY_PRESETS = {
  entityList: {
    staleTime: CACHE_TIMES.PAGINATION_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
    placeholderData: keepPreviousData,
  },
  userInfo: {
    staleTime: CACHE_TIMES.USER_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },
  staticData: {
    staleTime: CACHE_TIMES.STATIC_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },
  dynamicData: {
    staleTime: CACHE_TIMES.DYNAMIC_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },
  detailData: {
    staleTime: CACHE_TIMES.PAGINATION_DATA,
    gcTime: CACHE_TIMES.GC_TIME,
  },
} as const;
